import { useEffect, useState } from "react";
import { get, post } from "@/lib/api";
import { journeyById, canPerform, allowedRoleNames } from "@/data/canvas";
import { usePersona } from "@/context/PersonaContext";
import { JourneyShell } from "@/components/JourneyShell";
import { GovernedActionPanel } from "@/components/GovernedActionPanel";
import { LayerTag } from "@/components/LayerTag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Cell } from "recharts";
import { Info, Plus, Users, Clock, AlertTriangle, ShieldCheck, Lock } from "lucide-react";

const journey = journeyById("activity_return");

export default function ActivityReturnJourney() {
  const { persona } = usePersona();
  const [list, setList] = useState([]);
  const [pi, setPi] = useState(null);
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ group_name: "", activity: "", activity_date: "", attendance_count: 0, volunteer_hours: 0, feedback_summary: "", observation: "", issue_flag: false });

  const load = async () => {
    const [d, p] = await Promise.all([get("/mill/returns"), get("/mill/prove-improve")]);
    setList(d.returns);
    setPi(p);
    setSelId((c) => c || d.returns[0]?.id);
  };
  useEffect(() => { load(); }, []);

  const sel = list.find((r) => r.id === selId);
  const canSubmit = canPerform(persona.id, "activity_return.submit");
  const canReview = canPerform(persona.id, "activity_return.review");

  const submit = async () => {
    if (!form.group_name || !form.activity || !form.activity_date) { toast.error("Please complete the return."); return; }
    setBusy(true);
    const created = await post("/mill/returns", { ...form, attendance_count: Number(form.attendance_count), volunteer_hours: Number(form.volunteer_hours) });
    setShowForm(false);
    setForm({ group_name: "", activity: "", activity_date: "", attendance_count: 0, volunteer_hours: 0, feedback_summary: "", observation: "", issue_flag: false });
    await load();
    setSelId(created.id);
    toast.success("Return submitted. Aggregate evidence updated.");
    setBusy(false);
  };

  const review = async () => {
    setBusy(true);
    await post(`/mill/returns/${sel.id}/review`, {});
    await load();
    toast.success("Return marked reviewed.");
    setBusy(false);
  };

  return (
    <JourneyShell journey={journey}>
      {/* Prove & Improve aggregate */}
      {pi && (
        <div className="mb-6 rounded-2xl border border-stone-200 bg-white p-6" data-testid="prove-improve">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-lg font-semibold text-mill-ink">Prove & Improve — aggregate view</h2>
              <LayerTag layer="ordinary" />
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F0EA] px-3 py-1 text-xs font-semibold text-mill-moss"><Lock className="h-3.5 w-3.5" /> No participant data</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat icon={Users} label="Total attendance" value={pi.total_attendance} />
            <Stat icon={Clock} label="Volunteer hours" value={pi.total_volunteer_hours} />
            <Stat icon={ShieldCheck} label="Returns submitted" value={pi.returns_submitted} />
            <Stat icon={AlertTriangle} label="Issues logged" value={pi.issues_logged} />
          </div>
          <div className="mt-5 h-48" data-testid="pi-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pi.by_group} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <XAxis dataKey="group" tick={{ fontSize: 11, fill: "#57534E" }} tickLine={false} axisLine={{ stroke: "#E7E5E4" }} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickLine={false} axisLine={false} />
                <RTooltip cursor={{ fill: "rgba(74,93,78,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 12 }} />
                <Bar dataKey="attendance" radius={[6, 6, 0, 0]} isAnimationActive={false} maxBarSize={90}>
                  {pi.by_group.map((_, i) => <Cell key={i} fill="#4A5D4E" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-xs text-mill-muted">{pi.note}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        {/* Returns list */}
        <div className="space-y-3" data-testid="returns-list">
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-mill-muted">Activity returns</p>
            <button data-testid="new-return-toggle" onClick={() => setShowForm((s) => !s)} disabled={!canSubmit} title={canSubmit ? "New return" : `Only a ${allowedRoleNames("activity_return.submit")} can submit`} className="inline-flex items-center gap-1 rounded-full bg-mill-moss px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">
              <Plus className="h-3.5 w-3.5" /> New
            </button>
          </div>
          {!canSubmit && <p className="flex items-center gap-1.5 px-1 text-[11px] text-mill-muted"><Info className="h-3 w-3" /> Switch to Group Leader to file a return.</p>}

          {showForm && canSubmit && (
            <div className="space-y-2 rounded-xl border border-mill-moss/40 bg-white p-3" data-testid="return-form">
              <Input placeholder="Group name" value={form.group_name} onChange={(e) => setForm({ ...form, group_name: e.target.value })} data-testid="return-group" />
              <Input placeholder="Activity" value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} data-testid="return-activity" />
              <Input type="date" value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} data-testid="return-date" />
              <div className="flex gap-2">
                <Input type="number" placeholder="Attendance" value={form.attendance_count} onChange={(e) => setForm({ ...form, attendance_count: e.target.value })} data-testid="return-attendance" />
                <Input type="number" placeholder="Vol. hours" value={form.volunteer_hours} onChange={(e) => setForm({ ...form, volunteer_hours: e.target.value })} data-testid="return-hours" />
              </div>
              <Textarea placeholder="Feedback summary (no participant names)" value={form.feedback_summary} onChange={(e) => setForm({ ...form, feedback_summary: e.target.value })} data-testid="return-feedback" />
              <Textarea placeholder="Observation / issue" value={form.observation} onChange={(e) => setForm({ ...form, observation: e.target.value })} data-testid="return-observation" />
              <div className="flex items-center justify-between px-1">
                <Label className="text-sm text-mill-muted">Flag an issue</Label>
                <Switch checked={form.issue_flag} onCheckedChange={(v) => setForm({ ...form, issue_flag: v })} data-testid="return-issue" />
              </div>
              <Button className="w-full bg-mill-moss hover:bg-mill-moss-dark" onClick={submit} disabled={busy} data-testid="return-submit">Submit return</Button>
            </div>
          )}

          {list.map((r) => (
            <button key={r.id} data-testid={`return-select-${r.id}`} onClick={() => setSelId(r.id)} className={`w-full rounded-xl border p-3 text-left transition-colors ${selId === r.id ? "border-mill-moss bg-white shadow-sm" : "border-stone-200 bg-white/60 hover:bg-white"}`}>
              <p className="font-semibold text-mill-ink">{r.group_name}</p>
              <p className="text-xs text-mill-muted">{r.activity} · {r.activity_date}</p>
              <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${r.status === "reviewed" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEF3C7] text-[#B45309]"}`}>{r.status}</span>
            </button>
          ))}
        </div>

        {/* Detail */}
        {sel && (
          <div className="space-y-6" data-testid="return-detail">
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl font-semibold text-mill-ink">{sel.group_name}</h2>
                  <p className="text-sm text-mill-muted">{sel.activity} · {sel.activity_date} · by {sel.submitted_by}</p>
                </div>
                <LayerTag layer="self_service" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Stat icon={Users} label="Attendance (aggregate)" value={sel.attendance_count} />
                <Stat icon={Clock} label="Volunteer hours" value={sel.volunteer_hours} />
              </div>
              {sel.feedback_summary && <p className="mt-4 rounded-xl bg-mill-alt px-4 py-3 text-sm text-mill-ink"><span className="font-semibold">Feedback:</span> {sel.feedback_summary}</p>}
              {sel.observation && <p className="mt-2 rounded-xl bg-[#FEF3C7] px-4 py-3 text-sm text-[#92400E]"><span className="font-semibold">Observation:</span> {sel.observation}</p>}

              <div className="mt-5 border-t border-stone-200 pt-5">
                {sel.status === "reviewed" ? (
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#D1FAE5] px-3 py-1.5 text-sm font-semibold text-[#065F46]" data-testid="return-reviewed">Reviewed by trustee</span>
                ) : canReview ? (
                  <Button className="bg-mill-moss hover:bg-mill-moss-dark" onClick={review} disabled={busy} data-testid="review-button">Mark reviewed</Button>
                ) : (
                  <p className="flex items-center gap-1.5 text-xs text-mill-muted" data-testid="review-guard"><Info className="h-3.5 w-3.5" /> Only the {allowedRoleNames("activity_return.review")} reviews returns. Switch to Trustee.</p>
                )}
              </div>
            </div>

            <GovernedActionPanel
              journey="activity_return"
              refId={sel.id}
              action="activity_return.sign"
              governed={journey.governed}
            />
          </div>
        )}
      </div>
    </JourneyShell>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-mill-alt/40 p-3">
      <div className="flex items-center gap-1.5 text-mill-muted"><Icon className="h-3.5 w-3.5" /><span className="text-[11px] uppercase tracking-wider">{label}</span></div>
      <p className="mt-1 font-display text-2xl font-bold text-mill-ink">{value}</p>
    </div>
  );
}
